import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { FormInstance, Text } from 'rsuite';
import {
  selectConfigExpressAllTaxCollectionAgreementWith,
  selectConfigExpressTax,
  selectConfigTaxPaymentPeriod,
} from 'src/store/slices/config/configSelectors';
import { TaxReportContent } from 'src/components/tax/TaxReportContent';
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from 'src/common/enums';
import { constructMarkup, getDateRelatedBucket } from 'src/common/utils';
import CollectionAgreementNotice from 'src/components/tax/CollectionAgreementNotice';
import IconIndicator from 'src/components/user-feedback/IconIndicator';
import Notice from 'src/components/user-feedback/Notice';
import { selectTaxState } from 'src/store/slices/tax/taxSelectors';
import usePlaceNavigate from 'src/hooks/usePlaceNavigate';
import { selectCurrentStep } from 'src/store/slices/flow/flowSelectors';
import {
  setStepCompletedStatus,
  setCurrentStep,
} from 'src/store/slices/flow/flowSlice';
import {
  resetCalculateTax,
  updateTaxFlowData,
} from 'src/store/slices/tax/taxSlice';
import { calculateMultiPropertyTaxAsync } from 'src/store/slices/tax/taxThunks';
import {
  BucketData,
  PaymentPeriod,
  TaxDataModel,
  ReportingMethod,
} from 'src/types/tax';
import {
  TaxConfiguration,
  TaxConfigurationInformation,
} from 'src/types/configuration';
import { useAppDispatch } from 'src/store/store';
import { FLOW_DIRECTION } from 'src/types/flow';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { BulkTax } from 'src/components/tax/BulkTax';
import FlowLayout from 'src/components/layout/FlowLayout';

export const TaxReport = (): ReactElement => {
  const { isMobile } = useIsMobile();
  const dispatch = useAppDispatch();
  const taxForm = useRef<FormInstance>(null);
  const placeNavigate = usePlaceNavigate();
  const [flowDirection, setFlowDirection] = useState<FLOW_DIRECTION>();
  const {
    taxFlowData: formData,
    calculatedTaxErrorMsg,
    isCalculateTaxSuccess,
    isCalculateTaxInProgress,
  } = useSelector(selectTaxState);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const {
    tax_config: taxConfig,
    deductions,
    collection_agreement_bucket,
    report_revenue,
  } = useSelector(selectConfigExpressTax) as TaxConfiguration;

  const taxablePeriod = useSelector(selectConfigTaxPaymentPeriod);
  const allCollectionAgreements = useSelector(
    selectConfigExpressAllTaxCollectionAgreementWith
  );

  const selectedLicenses = useMemo(
    () => formData.selectedLicenses || [],
    [formData?.selectedLicenses]
  );

  const reportingMethod = useMemo(
    () => formData.reportingMethod || ReportingMethod.INDIVIDUAL,
    [formData?.reportingMethod]
  );

  const hasCollectionAgreement = useMemo(() => {
    if (!selectedLicenses[0]?.startDate) return false;
    if (collection_agreement_bucket?.enable) {
      const buckets = getDateRelatedBucket(
        collection_agreement_bucket.with,
        selectedLicenses[0].startDate
      );
      return buckets.length > 1;
    }
    return false;
  }, [selectedLicenses, collection_agreement_bucket]);

  const bucketPerPeriod: Record<string, Array<BucketData>> = useMemo(() => {
    return selectedLicenses.reduce((prev, selectedLicense) => {
      const licBuckets = getDateRelatedBucket(
        allCollectionAgreements,
        selectedLicense.startDate
      );
      return {
        ...prev,
        [selectedLicense.startDate]: licBuckets.map((bucket) => ({
          ...bucket,
          deductionsConfig: deductions,
        })),
      };
    }, {});
  }, [selectedLicenses, allCollectionAgreements, deductions]);

  useEffect(() => {
    dispatch(resetCalculateTax());
  }, [dispatch]);

  const handleNextClick = useCallback(async () => {
    setFlowDirection(FLOW_DIRECTION.FORWARD);
    if (reportingMethod === ReportingMethod.BULK && isCalculateTaxSuccess) {
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
      );
      dispatch(setCurrentStep(selectedCurrentStep + 1));
      placeNavigate('/tax-confirm-and-sign');
    } else {
      taxForm.current?.submit();
    }
  }, [
    taxForm,
    reportingMethod,
    isCalculateTaxSuccess,
    placeNavigate,
    selectedCurrentStep,
  ]);

  const handleBackClick = async () => {
    setFlowDirection(FLOW_DIRECTION.BACK);
    taxForm.current?.submit();
    return true;
  };

  const getUpdatedData = useCallback(
    (value: TaxDataModel): Array<PaymentPeriod> => {
      return selectedLicenses.map((period, pIndex) => {
        const formPeriod = value.periods?.[pIndex];
        return {
          ...period,
          licenses: period.licenses.map((license, index) => ({
            ...license,
            occupancy: formPeriod?.licenses?.[index].occupancy,
            buckets: formPeriod?.licenses?.[index].buckets.map((bucket) => ({
              ...bucket,
              revenue: Number(bucket.revenue),
              discounts: bucket.discounts || 0,
            })),
          })),
        };
      });
    },
    [selectedLicenses]
  );

  const executeSubmitProcess = useCallback(
    async (updatedSelectedLicenses: PaymentPeriod[]) => {
      const calculationSuccessful = await dispatch(
        calculateMultiPropertyTaxAsync(
          updatedSelectedLicenses,
          taxConfig as TaxConfigurationInformation[]
        )
      );

      if (calculationSuccessful) {
        dispatch(
          updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses })
        );
      }

      return calculationSuccessful;
    },
    [dispatch, taxConfig]
  );

  const handleBulkSubmit = useCallback(
    async (value: TaxDataModel) => {
      const updatedSelectedLicenses = getUpdatedData(value);
      await executeSubmitProcess(updatedSelectedLicenses);
    },
    [getUpdatedData, executeSubmitProcess]
  );

  const handleSubmit = async (value: TaxDataModel) => {
    dispatch(resetCalculateTax());
    // get updated data and save to redux
    const updatedSelectedLicenses = getUpdatedData(value);
    // wait for the calculations to run before continuing
    if (flowDirection === FLOW_DIRECTION.FORWARD) {
      const successful = await executeSubmitProcess(updatedSelectedLicenses);
      if (successful) {
        dispatch(
          setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
        );
        dispatch(setCurrentStep(selectedCurrentStep + 1));
        placeNavigate('/tax-confirm-and-sign');
      }
    } else {
      dispatch(
        updateTaxFlowData({ selectedLicenses: updatedSelectedLicenses })
      );
    }
  };

  const taxablePeriodsText = useMemo(() => {
    const reportRevenueSubHeading = report_revenue?.report_page_sub_heading;
    if (reportRevenueSubHeading) {
      return reportRevenueSubHeading;
    } else {
      const periodString =
        taxablePeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ||
        taxablePeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
          ? 'quarters'
          : 'months';
      return `For the current and previous ${periodString}, please report the taxable receipts.`;
    }
  }, [taxablePeriod, report_revenue]);

  return (
    <FlowLayout
      flow={FLOWS_ENUM.PAY_TOT}
      title="Report Revenue"
      omitNavigationForward
      showBack
      showNext
      onNext={handleNextClick}
      onBack={handleBackClick}
    >
      <Text className="text-large">{taxablePeriodsText}</Text>
      <Text className="paragraph text-small mb-24">
        {constructMarkup(report_revenue?.text ?? '')}
      </Text>
      {hasCollectionAgreement && (
        <CollectionAgreementNotice
          buckets={bucketPerPeriod[selectedLicenses[0].startDate]}
        />
      )}
      {reportingMethod === ReportingMethod.BULK && (
        <BulkTax
          deductions={deductions}
          periods={selectedLicenses}
          bucketPerPeriod={bucketPerPeriod}
          onSubmit={handleBulkSubmit}
          step1Description="Download this template and fill in Taxable Receipts and Allowable
            Deductions for each period. It has been pre-populated based on your
            selections on the previous screen. To edit your selections, click
            ‘Back’."
          step2Description="Upload your completed spreadsheet by clicking the button below. If
            there are any validation errors reported, please fix the spreadsheet
            and upload it again. Once uploaded successfully, click ‘Continue’ to proceed."
        />
      )}
      {reportingMethod === ReportingMethod.INDIVIDUAL && (
        <TaxReportContent
          bucketPerPeriod={bucketPerPeriod}
          deductions={deductions}
          isMobile={isMobile}
          licenses={selectedLicenses}
          onSubmit={handleSubmit}
          ref={taxForm}
        />
      )}
      {!isCalculateTaxSuccess && calculatedTaxErrorMsg && (
        <Notice message={calculatedTaxErrorMsg} />
      )}
      {isCalculateTaxSuccess && reportingMethod === ReportingMethod.BULK && (
        <Notice
          message="Report validated successfully - click 'Continue' to proceed"
          type="success"
        />
      )}
      {isCalculateTaxInProgress && <IconIndicator />}
    </FlowLayout>
  );
};
